import { auth, db, fs } from './firebase.js';

const BOOK_CACHE_KEY = 'mta_v2_books_cache';
const CART_KEY = 'mta_v2_cart';
const CUSTOMER_KEY = 'mta_v2_customer_phone';
const ADMIN_KEY = 'mta_v2_admin';
const OTP_SESSION_KEY = 'mta_v2_otp_phone';
const CF_BASE = 'https://us-central1-muppadai-book-store.cloudfunctions.net';
const CF_SEND = CF_BASE + '/sendOTP';
const CF_VERIFY = CF_BASE + '/verifyOTP';
const CF_RESEND = CF_BASE + '/resendOTP';
export const PABBLY_WEBHOOK_URL = 'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjcwNTZmMDYzMjA0M2Q1MjZlNTUzNjUxMzMi_pc';
export const ADMIN_PASSWORD = 'Mtabooks@26';
export const DEFAULT_RAZORPAY_KEY = 'rzp_live_SfdjYZrrWfHcwN';
export const RAZORPAY_KEY = DEFAULT_RAZORPAY_KEY;

export function money(value) {
  return 'Rs.' + Number(value || 0).toLocaleString('en-IN');
}

export function cleanPhone(value) {
  return String(value || '').replace(/\D/g, '').slice(-10);
}

export function dateLabel(value) {
  if (!value) return '-';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function isSoftDeletedBook(book) {
  return book?.deleteType === 'soft_delete';
}

export function isTrashedOrder(order) {
  return order?.deleteType === 'trash' || order?.trashed === true || order?.deleted === true;
}

export function sanitizePublicBook(book) {
  const next = { ...(book || {}) };
  if (next.ebook && typeof next.ebook === 'object') {
    const { downloadUrl, ...safeEbook } = next.ebook;
    next.ebook = safeEbook;
  }
  delete next.ebookDownloadUrl;
  return next;
}

export function normalizeBooks(docs) {
  return docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .filter((book) => book.active !== false && !isSoftDeletedBook(book))
    .map(sanitizePublicBook)
    .sort((a, b) => Number(a.order || 99) - Number(b.order || 99));
}

export function normalizeAdminBooks(docs) {
  return docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .filter((book) => !isSoftDeletedBook(book))
    .sort((a, b) => Number(a.order || 99) - Number(b.order || 99));
}

export function readBooksCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(BOOK_CACHE_KEY) || 'null');
    return Array.isArray(cached?.items) ? cached.items.map(sanitizePublicBook) : [];
  } catch {
    return [];
  }
}

export function writeBooksCache(items) {
  try {
    localStorage.setItem(BOOK_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), items: (items || []).map(sanitizePublicBook) }));
  } catch {
    // local storage is optional
  }
}

export function readCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY) || '[]');
  } catch {
    return [];
  }
}

export function writeCart(cart) {
  localStorage.setItem(CART_KEY, JSON.stringify(cart || []));
}

export function getCustomerPhone() {
  return cleanPhone(localStorage.getItem(CUSTOMER_KEY));
}

export function setCustomerPhone(phone) {
  const clean = cleanPhone(phone);
  if (clean) localStorage.setItem(CUSTOMER_KEY, clean);
  else localStorage.removeItem(CUSTOMER_KEY);
  return clean;
}

export function getOtpSessionPhone() {
  return cleanPhone(sessionStorage.getItem(OTP_SESSION_KEY) || localStorage.getItem(OTP_SESSION_KEY));
}

export function setOtpSessionPhone(phone) {
  const clean = cleanPhone(phone);
  if (clean) {
    sessionStorage.setItem(OTP_SESSION_KEY, clean);
    localStorage.setItem(OTP_SESSION_KEY, clean);
  } else {
    sessionStorage.removeItem(OTP_SESSION_KEY);
    localStorage.removeItem(OTP_SESSION_KEY);
  }
  return clean;
}

export function isValidIndianMobile(phone) {
  return /^[6-9]\d{9}$/.test(cleanPhone(phone));
}

export function isAdminLoggedIn() {
  return sessionStorage.getItem(ADMIN_KEY) === '1';
}

export function setAdminLoggedIn(active) {
  if (active) sessionStorage.setItem(ADMIN_KEY, '1');
  else sessionStorage.removeItem(ADMIN_KEY);
}

export async function loadBooks() {
  const cached = readBooksCache();
  const snap = await fs.getDocs(fs.collection(db, 'books'));
  const books = normalizeBooks(snap.docs);
  writeBooksCache(books);
  return books.length ? books : cached;
}

export async function loadBookRatings() {
  const snap = await fs.getDocs(fs.collection(db, 'bookRatings'));
  const grouped = {};
  snap.docs.forEach((docSnap) => {
    const item = { id: docSnap.id, ...docSnap.data() };
    if (item.active === false || item.hidden === true) return;
    if (!item.bookId) return;
    grouped[item.bookId] = grouped[item.bookId] || { total: 0, count: 0, reviews: [] };
    grouped[item.bookId].total += Number(item.rating || 0);
    grouped[item.bookId].count += 1;
    grouped[item.bookId].reviews.push(item);
  });
  Object.keys(grouped).forEach((bookId) => {
    grouped[bookId].average = grouped[bookId].count ? grouped[bookId].total / grouped[bookId].count : 0;
    grouped[bookId].reviews.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  });
  return grouped;
}

export async function saveBookRating({ bookId, bookTitle, phone, orderId, rating, review = '' }) {
  const clean = cleanPhone(phone);
  const stars = Math.max(1, Math.min(5, Number(rating || 0)));
  if (!bookId) throw new Error('Book is missing.');
  if (!clean) throw new Error('Login is required to rate.');
  if (!stars) throw new Error('Select a rating.');
  const payload = {
    bookId,
    bookTitle: bookTitle || '',
    phone: clean,
    orderId: orderId || '',
    rating: stars,
    review: review.trim().slice(0, 500),
    verifiedPurchase: true,
    active: true,
    hidden: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  await fs.setDoc(fs.doc(db, 'bookRatings', `${bookId}_${clean}`), payload, { merge: true });

  const ratings = await loadBookRatings();
  const stat = ratings[bookId] || { average: stars, count: 1 };
  await fs.setDoc(
    fs.doc(db, 'books', bookId),
    {
      ratingAverage: Number(stat.average || stars),
      ratingCount: Number(stat.count || 1),
      updatedAt: new Date().toISOString()
    },
    { merge: true }
  );
  return payload;
}

async function refreshBookRatingStats(bookId) {
  if (!bookId) return;
  const ratings = await loadBookRatings();
  const stat = ratings[bookId] || { average: 0, count: 0 };
  await fs.setDoc(
    fs.doc(db, 'books', bookId),
    {
      ratingAverage: Number(stat.average || 0),
      ratingCount: Number(stat.count || 0),
      updatedAt: new Date().toISOString()
    },
    { merge: true }
  );
}

export async function saveAdminReview({ id, bookId, bookTitle, studentName, phone, orderId, rating, review, active = true }) {
  const stars = Math.max(1, Math.min(5, Number(rating || 0)));
  if (!bookId) throw new Error('Select a book.');
  if (!stars) throw new Error('Select a rating.');
  const now = new Date().toISOString();
  const reviewId = id || `${bookId}_admin_${Date.now()}`;
  const payload = {
    bookId,
    bookTitle: bookTitle || '',
    studentName: studentName || 'Verified Student',
    phone: cleanPhone(phone) || '',
    orderId: orderId || '',
    rating: stars,
    review: String(review || '').trim().slice(0, 500),
    verifiedPurchase: true,
    active: active !== false,
    hidden: active === false,
    updatedAt: now
  };
  if (!id) payload.createdAt = now;
  await fs.setDoc(fs.doc(db, 'bookRatings', reviewId), payload, { merge: true });
  await refreshBookRatingStats(bookId);
}

export async function setAdminReviewActive(reviewId, bookId, active) {
  if (!reviewId) return;
  await fs.setDoc(
    fs.doc(db, 'bookRatings', reviewId),
    { active: active !== false, hidden: active === false, updatedAt: new Date().toISOString() },
    { merge: true }
  );
  await refreshBookRatingStats(bookId);
}

export async function loadAdminData() {
  const [ordersSnap, studentsSnap, booksSnap, couponsSnap, settingsSnap, recoverySnap, notificationsSnap, ratingsSnap] = await Promise.all([
    fs.getDocs(fs.collection(db, 'orders')),
    fs.getDocs(fs.collection(db, 'students')),
    fs.getDocs(fs.collection(db, 'books')),
    fs.getDocs(fs.collection(db, 'coupons')),
    fs.getDoc(fs.doc(db, 'settings', 'site')),
    fs.getDocs(fs.collection(db, 'paymentRecovery')),
    fs.getDocs(fs.collection(db, 'notifications')),
    fs.getDocs(fs.collection(db, 'bookRatings'))
  ]);

  const allOrders = ordersSnap.docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const orders = allOrders.filter((order) => !isTrashedOrder(order));
  const trashedOrders = allOrders.filter(isTrashedOrder);
  const students = studentsSnap.docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .sort((a, b) => String(b.lastOrderDate || b.updatedAt || '').localeCompare(String(a.lastOrderDate || a.updatedAt || '')));
  const books = normalizeAdminBooks(booksSnap.docs);
  const coupons = couponsSnap.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
  const settings = settingsSnap.exists() ? settingsSnap.data() : {};
  const paymentRecovery = recoverySnap.docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const notifications = notificationsSnap.docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  const bookRatings = ratingsSnap.docs
    .map((snap) => ({ id: snap.id, ...snap.data() }))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

  return { orders, trashedOrders, students, books, coupons, settings, paymentRecovery, notifications, bookRatings };
}

export async function loadCustomerOrders(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return [];
  const snap = await fs.getDocs(fs.query(fs.collection(db, 'orders'), fs.where('studentPhone', '==', clean)));
  return snap.docs
    .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
    .filter((order) => !isTrashedOrder(order))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

export async function loadStudent(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return null;
  const snap = await fs.getDoc(fs.doc(db, 'students', clean));
  return snap.exists() ? { id: snap.id, ...snap.data() } : { id: clean, phone: clean };
}

export async function loadStudentProfileWithLastOrder(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return { student: null, lastOrder: null };
  const [student, orders] = await Promise.all([loadStudent(clean), loadCustomerOrders(clean)]);
  return { student, lastOrder: orders[0] || null };
}

export async function loadSiteSettings() {
  const snap = await fs.getDoc(fs.doc(db, 'settings', 'site'));
  return snap.exists() ? snap.data() : {};
}

export async function saveStudentAddress(phone, delivery) {
  const clean = cleanPhone(phone);
  if (!clean) throw new Error('Enter a valid mobile number.');
  const payload = {
    name: delivery.name || '',
    phone: clean,
    email: delivery.email || '',
    address: delivery.address || '',
    street: delivery.street || '',
    postOffice: delivery.postOffice || '',
    pincode: delivery.pincode || '',
    district: delivery.district || '',
    state: delivery.state || '',
    altPhone: delivery.altPhone || '',
    updatedAt: new Date().toISOString()
  };
  await fs.setDoc(fs.doc(db, 'students', clean), payload, { merge: true });
  return payload;
}

export async function loadNotifications(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return [];
  const snap = await fs.getDocs(fs.query(fs.collection(db, 'notifications'), fs.where('phone', '==', clean)));
  return snap.docs
    .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .slice(0, 20);
}

export async function markNotificationsRead(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return;
  const snap = await fs.getDocs(fs.query(fs.collection(db, 'notifications'), fs.where('phone', '==', clean), fs.where('read', '==', false)));
  await Promise.all(snap.docs.map((docSnap) => fs.setDoc(fs.doc(db, 'notifications', docSnap.id), { read: true, readAt: new Date().toISOString() }, { merge: true })));
}

async function postOtp(url, payload) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'OTP request failed. Please try again.');
  }
  return data;
}

export async function sendOtp(phone) {
  const clean = cleanPhone(phone);
  if (!isValidIndianMobile(clean)) throw new Error('Enter a valid 10-digit mobile number.');
  return postOtp(CF_SEND, { phone: clean });
}

export async function resendOtp(phone) {
  const clean = cleanPhone(phone);
  if (!isValidIndianMobile(clean)) throw new Error('Enter a valid 10-digit mobile number.');
  return postOtp(CF_RESEND, { phone: clean });
}

export async function verifyOtp(phone, otp) {
  const clean = cleanPhone(phone);
  const code = String(otp || '').replace(/\D/g, '').slice(0, 6);
  if (!isValidIndianMobile(clean)) throw new Error('Enter a valid 10-digit mobile number.');
  if (code.length !== 6) throw new Error('Enter the 6-digit OTP.');
  const data = await postOtp(CF_VERIFY, { phone: clean, otp: code });
  setCustomerPhone(clean);
  setOtpSessionPhone(clean);
  if (data.customToken) {
    fs.signInWithCustomToken(auth, data.customToken).catch(() => {});
  }
  return data;
}

export async function lookupPincode(pin) {
  const clean = String(pin || '').replace(/\D/g, '').slice(0, 6);
  if (clean.length !== 6) throw new Error('Enter a valid 6-digit pincode.');
  const res = await fetch('https://api.postalpincode.in/pincode/' + clean);
  const data = await res.json();
  const offices = data?.[0]?.PostOffice || [];
  if (data?.[0]?.Status !== 'Success' || !offices.length) {
    throw new Error('Invalid pincode. Please check and re-enter.');
  }
  return {
    pincode: clean,
    district: offices[0].District || '',
    state: offices[0].State || '',
    offices
  };
}

export function orderItemsFromCart(cart) {
  return (cart || []).map((item) => {
    const productType = item.productType || 'physical';
    const isEbookOnly = String(productType).toLowerCase() === 'ebook';
    return {
      bid: item.bid || '',
      bt: item.bt || item.bookTitle || '',
      bookTitle: item.bookTitle || item.bt || '',
      vn: item.vn || item.variantName || '',
      variantName: item.variantName || item.vn || '',
      qty: isEbookOnly ? 1 : Number(item.qty || 1),
      price: Number(item.price || 0),
      productType,
      ebookFileName: item.ebookFileName || '',
      ebookAccessNote: item.ebookAccessNote || ''
    };
  });
}

export function orderTotal(items) {
  return (items || []).reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1), 0);
}

export function orderFulfillmentFromItems(items) {
  const list = items || [];
  const hasEbook = list.some((item) => ['ebook', 'combo'].includes(String(item.productType || '').toLowerCase()));
  const hasPhysical = list.some((item) => {
    const type = String(item.productType || 'physical').toLowerCase();
    return type === 'physical' || type === 'combo' || !type;
  });
  if (hasEbook && hasPhysical) return 'mixed';
  if (hasEbook) return 'ebook';
  return 'physical';
}

export async function createCheckoutDraft({ delivery, cart }) {
  const phone = cleanPhone(delivery.phone);
  const items = orderItemsFromCart(cart);
  const total = orderTotal(items);
  const fulfillmentType = orderFulfillmentFromItems(items);
  const payload = {
    studentPhone: phone,
    delivery: {
      name: delivery.name || '',
      phone,
      address: delivery.address || '',
      altPhone: delivery.altPhone || '',
      email: delivery.email || '',
      street: delivery.street || '',
      postOffice: delivery.postOffice || '',
      pincode: delivery.pincode || '',
      district: delivery.district || '',
      state: delivery.state || ''
    },
    items,
    total,
    fulfillmentType,
    hasEbook: fulfillmentType === 'ebook' || fulfillmentType === 'mixed',
    hasPhysical: fulfillmentType === 'physical' || fulfillmentType === 'mixed',
    status: 'checkout_started',
    orderCreated: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    source: 'react_platform_checkout'
  };
  const ref = await fs.addDoc(fs.collection(db, 'checkoutDrafts'), payload);
  return { id: ref.id, ...payload };
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readOrderDoc(orderDocId) {
  const clean = String(orderDocId || '').trim();
  if (!clean) return null;
  const snap = await fs.getDoc(fs.doc(db, 'orders', clean));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function findOrderByPayment(paymentId) {
  if (!paymentId) return null;
  const snap = await fs.getDocs(fs.query(fs.collection(db, 'orders'), fs.where('paymentId', '==', paymentId), fs.limit(1)));
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

export async function findOrderByCheckoutDraft(draftId) {
  const cleanDraftId = String(draftId || '').trim();
  if (!cleanDraftId) return null;

  try {
    const draftSnap = await fs.getDoc(fs.doc(db, 'checkoutDrafts', cleanDraftId));
    if (draftSnap.exists()) {
      const draft = draftSnap.data();
      const orderFromDraft = await readOrderDoc(draft.orderDocId);
      if (orderFromDraft) return orderFromDraft;
      if (draft.paymentId) {
        const orderFromPayment = await findOrderByPayment(draft.paymentId);
        if (orderFromPayment) return orderFromPayment;
      }
    }
  } catch {
    // Fall back to querying orders by checkoutDraftId below.
  }

  const snap = await fs.getDocs(fs.query(fs.collection(db, 'orders'), fs.where('checkoutDraftId', '==', cleanDraftId), fs.limit(1)));
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

async function findExistingPaidOrder(paymentId, draftId) {
  return (await findOrderByPayment(paymentId)) || (await findOrderByCheckoutDraft(draftId));
}

async function waitForExistingPaidOrder(paymentId, draftId, { attempts = 10, delayMs = 800 } = {}) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const existing = await findExistingPaidOrder(paymentId, draftId);
    if (existing) return existing;
    if (attempt < attempts - 1) await sleep(delayMs);
  }
  return null;
}

async function savePaymentRecoveryFromCheckout({ paymentId, delivery, items, total, draftId, status, order }) {
  const cleanPaymentId = String(paymentId || '').trim();
  if (!cleanPaymentId) return;
  const now = new Date().toISOString();
  try {
    await fs.setDoc(
      fs.doc(db, 'paymentRecovery', cleanPaymentId),
      {
        paymentId: cleanPaymentId,
        delivery,
        items,
        total,
        checkoutDraftId: draftId || '',
        draftId: draftId || '',
        status,
        orderDocId: order?.id || '',
        orderId: order?.orderId || '',
        updatedAt: now,
        lastSeenAt: now,
        source: 'react_platform_checkout'
      },
      { merge: true }
    );
  } catch {
    // Recovery logging is helpful, but order handling must continue if it fails.
  }
}

export async function createPaidOrderSafe({ paymentId, cart, delivery, draftId }) {
  const cleanPaymentId = String(paymentId || '').trim();
  if (!cleanPaymentId) throw new Error('Missing payment ID.');

  const phone = cleanPhone(delivery.phone);
  const items = orderItemsFromCart(cart);
  const total = orderTotal(items);
  const fulfillmentType = orderFulfillmentFromItems(items);
  const cleanDelivery = {
    name: delivery.name || '',
    phone,
    address: delivery.address || '',
    altPhone: delivery.altPhone || '',
    email: delivery.email || '',
    street: delivery.street || '',
    postOffice: delivery.postOffice || '',
    pincode: delivery.pincode || '',
    district: delivery.district || '',
    state: delivery.state || ''
  };

  await savePaymentRecoveryFromCheckout({
    paymentId: cleanPaymentId,
    delivery: cleanDelivery,
    items,
    total,
    draftId,
    status: 'payment_success_waiting_for_webhook'
  });

  const existing = await waitForExistingPaidOrder(cleanPaymentId, draftId);
  if (existing) {
    await savePaymentRecoveryFromCheckout({
      paymentId: cleanPaymentId,
      delivery: cleanDelivery,
      items,
      total,
      draftId,
      status: existing.source === 'razorpay_webhook' ? 'order_created_by_webhook' : 'already_exists',
      order: existing
    });
    await ensurePaymentSuccessWebhook(existing);
    return existing;
  }

  let orderNum = 1000;

  try {
    await fs.runTransaction(db, async (transaction) => {
      const counterRef = fs.doc(db, 'settings', 'orderCounter');
      const counterSnap = await transaction.get(counterRef);
      orderNum = counterSnap.exists() ? Number(counterSnap.data().count || 999) + 1 : 1000;
      transaction.set(counterRef, { count: orderNum }, { merge: true });
    });
  } catch {
    orderNum = 1000 + Math.floor(Math.random() * 900000);
  }

  const order = {
    paymentId: cleanPaymentId,
    items,
    total,
    delivery: cleanDelivery,
    createdAt: new Date().toISOString(),
    status: 'paid',
    deliveryStatus: 'processing',
    fulfillmentType,
    hasEbook: fulfillmentType === 'ebook' || fulfillmentType === 'mixed',
    hasPhysical: fulfillmentType === 'physical' || fulfillmentType === 'mixed',
    ebookAccessStatus: fulfillmentType === 'ebook' || fulfillmentType === 'mixed' ? 'ready' : '',
    studentPhone: phone,
    orderId: '#' + orderNum,
    checkoutDraftId: draftId || '',
    source: 'react_platform_safe'
  };

  const ref = await fs.addDoc(fs.collection(db, 'orders'), order);
  await fs.setDoc(
    fs.doc(db, 'students', phone),
    {
      name: delivery.name || '',
      phone,
      email: delivery.email || '',
      address: delivery.address || '',
      street: delivery.street || '',
      postOffice: delivery.postOffice || '',
      pincode: delivery.pincode || '',
      district: delivery.district || '',
      state: delivery.state || '',
      altPhone: delivery.altPhone || '',
      lastOrderDate: new Date().toISOString(),
      source: 'online',
      updatedAt: new Date().toISOString()
    },
    { merge: true }
  );

  if (draftId) {
    await fs.setDoc(
      fs.doc(db, 'checkoutDrafts', draftId),
      {
        orderCreated: true,
        orderDocId: ref.id,
        orderId: order.orderId,
        paymentId: cleanPaymentId,
        status: 'order_created',
        updatedAt: new Date().toISOString()
      },
      { merge: true }
    );
  }

  try {
    await sendPaymentSuccessWebhook({ id: ref.id, ...order });
  } catch {
    // Never block order creation if an external automation endpoint is down.
  }
  const createdOrder = { id: ref.id, ...order };
  await savePaymentRecoveryFromCheckout({
    paymentId: cleanPaymentId,
    delivery: cleanDelivery,
    items,
    total,
    draftId,
    status: 'order_created',
    order: createdOrder
  });
  return createdOrder;
}

export async function recoverPaymentRecord(record) {
  const paymentId = record.paymentId || record.id;
  if (!paymentId) throw new Error('Missing payment ID.');
  const existing = await findOrderByPayment(paymentId);
  if (existing) {
    await fs.setDoc(fs.doc(db, 'paymentRecovery', record.id || paymentId), { status: 'already_exists', orderDocId: existing.id, orderId: existing.orderId || '', updatedAt: new Date().toISOString() }, { merge: true });
    return existing;
  }
  const delivery = record.delivery || {};
  const order = await createPaidOrderSafe({
    paymentId,
    cart: record.items || [],
    delivery: {
      name: delivery.name || record.name || '',
      phone: delivery.phone || record.studentPhone || record.phone || '',
      email: delivery.email || '',
      altPhone: delivery.altPhone || '',
      address: delivery.address || record.address || '',
      street: delivery.street || '',
      postOffice: delivery.postOffice || '',
      pincode: delivery.pincode || '',
      district: delivery.district || '',
      state: delivery.state || 'Tamil Nadu'
    },
    draftId: record.checkoutDraftId || record.draftId || ''
  });
  await fs.setDoc(fs.doc(db, 'paymentRecovery', record.id || paymentId), { status: 'order_created_by_admin', orderDocId: order.id, orderId: order.orderId || '', updatedAt: new Date().toISOString() }, { merge: true });
  return order;
}

export async function updateOrderStatus(orderId, status) {
  const now = new Date().toISOString();
  const payload = {
    deliveryStatus: status,
    updatedAt: now
  };
  if (status === 'shipped') payload.shippedAt = now;
  if (status === 'delivered') payload.deliveredAt = now;
  await fs.updateDoc(fs.doc(db, 'orders', orderId), payload);
}

export async function softDeleteOrder(orderId) {
  const now = new Date().toISOString();
  await fs.setDoc(
    fs.doc(db, 'orders', orderId),
    {
      deleted: true,
      trashed: true,
      deleteType: 'trash',
      deletedAt: now,
      updatedAt: now
    },
    { merge: true }
  );
}

export async function restoreOrder(orderId) {
  const now = new Date().toISOString();
  await fs.setDoc(
    fs.doc(db, 'orders', orderId),
    {
      deleted: false,
      trashed: false,
      deleteType: '',
      restoredAt: now,
      updatedAt: now
    },
    { merge: true }
  );
}

export function professionalCourierTrackingUrl(trackingNumber) {
  const clean = String(trackingNumber || '').trim();
  return clean ? `/track?number=${encodeURIComponent(clean)}` : '/track';
}

export async function updateOrderTracking(orderId, trackingNumber) {
  const clean = String(trackingNumber || '').trim();
  if (!clean) throw new Error('Tracking number is required.');
  const now = new Date().toISOString();
  const payload = {
    trackingNumber: clean,
    trackingCarrier: 'professional-couriers',
    trackingCarrierName: 'Professional Couriers',
    trackingUrl: professionalCourierTrackingUrl(clean),
    deliveryStatus: 'shipped',
    shippedAt: now,
    updatedAt: now
  };
  await fs.setDoc(fs.doc(db, 'orders', orderId), payload, { merge: true });
  return payload;
}

export async function createManualOrder({ delivery, items, paymentId, deliveryStatus = 'processing' }) {
  const phone = cleanPhone(delivery.phone);
  if (!delivery.name) throw new Error('Student name is required.');
  if (!isValidIndianMobile(phone)) throw new Error('Enter a valid 10-digit mobile number.');
  if (!delivery.street && !delivery.address) throw new Error('Address is required.');
  if (!String(delivery.pincode || '').match(/^\d{6}$/)) throw new Error('Enter a valid 6-digit pincode.');
  if (!delivery.district) throw new Error('District is required.');
  const orderItems = orderItemsFromCart(items);
  if (!orderItems.length) throw new Error('Add at least one book.');

  let orderNum = 1000;
  try {
    await fs.runTransaction(db, async (transaction) => {
      const counterRef = fs.doc(db, 'settings', 'orderCounter');
      const counterSnap = await transaction.get(counterRef);
      orderNum = counterSnap.exists() ? Number(counterSnap.data().count || 999) + 1 : 1000;
      transaction.set(counterRef, { count: orderNum }, { merge: true });
    });
  } catch {
    orderNum = 1000 + Math.floor(Math.random() * 900000);
  }

  const total = orderTotal(orderItems);
  const fulfillmentType = orderFulfillmentFromItems(orderItems);
  const address =
    delivery.address ||
    [delivery.street, delivery.postOffice, delivery.district, delivery.state || 'Tamil Nadu'].filter(Boolean).join(', ') + (delivery.pincode ? ' - ' + delivery.pincode : '');
  const now = new Date().toISOString();
  const order = {
    orderId: '#' + orderNum,
    paymentId: paymentId || 'MANUAL-' + orderNum,
    items: orderItems,
    total,
    isManual: true,
    source: 'manual_entry',
    status: 'paid',
    deliveryStatus,
    fulfillmentType,
    hasEbook: fulfillmentType === 'ebook' || fulfillmentType === 'mixed',
    hasPhysical: fulfillmentType === 'physical' || fulfillmentType === 'mixed',
    ebookAccessStatus: fulfillmentType === 'ebook' || fulfillmentType === 'mixed' ? 'ready' : '',
    studentPhone: phone,
    delivery: {
      name: delivery.name || '',
      phone,
      email: delivery.email || '',
      altPhone: delivery.altPhone || '',
      address,
      street: delivery.street || '',
      postOffice: delivery.postOffice || '',
      pincode: delivery.pincode || '',
      district: delivery.district || '',
      state: delivery.state || 'Tamil Nadu'
    },
    createdAt: now,
    updatedAt: now
  };
  const ref = await fs.addDoc(fs.collection(db, 'orders'), order);
  await saveAdminStudent({
    ...order.delivery,
    address,
    source: 'manual_entry',
    lastOrderDate: now
  });
  try {
    await sendPaymentSuccessWebhook({ id: ref.id, ...order });
  } catch {
    // Never block manual order creation if an external automation endpoint is down.
  }
  return { id: ref.id, ...order };
}

export async function saveAdminStudent(student) {
  const phone = cleanPhone(student.phone || student.id);
  if (!student.name) throw new Error('Student name is required.');
  if (!isValidIndianMobile(phone)) throw new Error('Enter a valid 10-digit mobile number.');
  const address =
    student.address ||
    [student.street, student.postOffice, student.district, student.state].filter(Boolean).join(', ') + (student.pincode ? ' - ' + student.pincode : '');
  const payload = {
    name: student.name || '',
    phone,
    email: student.email || '',
    address,
    street: student.street || '',
    postOffice: student.postOffice || '',
    pincode: student.pincode || '',
    district: student.district || '',
    state: student.state || 'Tamil Nadu',
    dob: student.dob || '',
    altPhone: student.altPhone || '',
    source: student.source || 'admin_added',
    updatedAt: new Date().toISOString()
  };
  if (student.lastOrderDate) payload.lastOrderDate = student.lastOrderDate;
  if (!student.id) payload.createdAt = new Date().toISOString();
  await fs.setDoc(fs.doc(db, 'students', phone), payload, { merge: true });
  return { id: phone, ...payload };
}

export async function saveAdminBook(book) {
  if (!book.title) throw new Error('Book title is required.');
  if (!book.cat) throw new Error('Category is required.');
  const productType = book.productType || (book.ebook?.enabled ? 'ebook' : 'physical');
  const variants = (book.variants || [])
    .map((variant) => ({
      name: variant.name || variant.vn || '',
      price: Number(variant.price || 0),
      inStock: variant.inStock !== false,
      imageUrl: variant.imageUrl || '',
      samplePdfUrl: variant.samplePdfUrl || '',
      restockDate: variant.restockDate || '',
      restockMsg: variant.restockMsg || ''
    }))
    .filter((variant) => variant.name && variant.price > 0);
  if (!variants.length) throw new Error('Add at least one variant.');
  const payload = {
    title: book.title || '',
    cat: book.cat || 'TNUSRB',
    productType,
    seoSlug: String(book.seoSlug || '').trim().toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 90),
    seoTitle: String(book.seoTitle || '').trim().slice(0, 90),
    metaDescription: String(book.metaDescription || '').trim().slice(0, 180),
    focusKeywords: String(book.focusKeywords || '').trim().slice(0, 240),
    description: book.description || '',
    detailPoints: Array.isArray(book.detailPoints)
      ? book.detailPoints.map((item) => String(item || '').trim()).filter(Boolean)
      : String(book.detailPoints || '').split('\n').map((item) => item.trim()).filter(Boolean),
    sampleImages: Array.isArray(book.sampleImages)
      ? book.sampleImages.map((item) => String(item || '').trim()).filter(Boolean)
      : String(book.sampleImages || '').split('\n').map((item) => item.trim()).filter(Boolean),
    faqs: Array.isArray(book.faqs)
      ? book.faqs.map((item) => ({ q: item.q || item.question || '', a: item.a || item.answer || '' })).filter((item) => item.q && item.a)
      : String(book.faqs || '').split('\n').map((line) => {
        const [q, ...rest] = line.split('|');
        return { q: String(q || '').trim(), a: rest.join('|').trim() };
      }).filter((item) => item.q && item.a),
    imageUrl: book.imageUrl || '',
    order: Number(book.order || 99),
    active: book.active !== false,
    inStock: book.inStock !== false,
    variants,
    ebook: {
      enabled: productType === 'ebook' || productType === 'combo',
      format: String(book.ebook?.format || book.ebookFormat || '').trim() || 'PDF',
      pages: String(book.ebook?.pages || book.ebookPages || '').trim(),
      sampleUrl: String(book.ebook?.sampleUrl || book.ebookSampleUrl || '').trim(),
      downloadUrl: String(book.ebook?.downloadUrl || book.ebookDownloadUrl || '').trim(),
      fileName: String(book.ebook?.fileName || book.ebookFileName || '').trim(),
      deliveryMode: String(book.ebook?.deliveryMode || book.ebookDeliveryMode || '').trim(),
      accessNote: String(book.ebook?.accessNote || book.ebookAccessNote || '').trim().slice(0, 600),
      licenseText: String(book.ebook?.licenseText || book.ebookLicenseText || '').trim().slice(0, 400)
    },
    updatedAt: new Date().toISOString()
  };
  if (book.id) {
    await fs.setDoc(fs.doc(db, 'books', book.id), payload, { merge: true });
    return { id: book.id, ...payload };
  }
  payload.createdAt = new Date().toISOString();
  const ref = await fs.addDoc(fs.collection(db, 'books'), payload);
  return { id: ref.id, ...payload };
}

export async function softDeleteBook(bookId) {
  await fs.setDoc(
    fs.doc(db, 'books', bookId),
    { active: false, deleteType: 'soft_delete', deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    { merge: true }
  );
}

export async function saveAdminCoupon(coupon) {
  const code = String(coupon.id || coupon.code || '').trim().toUpperCase();
  if (!code) throw new Error('Coupon code is required.');
  if (!Number(coupon.value)) throw new Error('Discount value is required.');
  const payload = {
    type: coupon.type || 'percent',
    value: Number(coupon.value || 0),
    desc: coupon.desc || '',
    active: coupon.active !== false,
    updatedAt: new Date().toISOString()
  };
  await fs.setDoc(fs.doc(db, 'coupons', code), payload, { merge: true });
  return { id: code, ...payload };
}

export async function toggleAdminCoupon(couponId, active) {
  await fs.setDoc(fs.doc(db, 'coupons', couponId), { active, updatedAt: new Date().toISOString() }, { merge: true });
}

export async function saveSiteSettings(settings) {
  const payload = { updatedAt: new Date().toISOString() };
  for (const field of ['gaId', 'fbPixelId', 'razorpayKey', 'logoUrl', 'faviconUrl', 'siteName', 'tagline', 'paymentWebhookUrl', 'shippedWebhookUrl']) {
    if (Object.prototype.hasOwnProperty.call(settings, field)) payload[field] = settings[field] || '';
  }
  if (Object.prototype.hasOwnProperty.call(settings, 'sitePages')) payload.sitePages = settings.sitePages;
  await fs.setDoc(fs.doc(db, 'settings', 'site'), payload, { merge: true });
  return payload;
}

export async function sendAdminNotification({ audience = 'all', title, body, bookTitle = '', district = '', students = [], orders = [] }) {
  if (!title || !body) throw new Error('Notification title and message are required.');
  const now = new Date().toISOString();
  const buyers = new Set();
  if (audience === 'book' && bookTitle) {
    orders.forEach((order) => {
      const hasBook = (order.items || []).some((item) => [item.bt, item.bookTitle, item.vn, item.variantName].join(' ').toLowerCase().includes(bookTitle.toLowerCase()));
      if (hasBook) buyers.add(orderPhoneFromAny(order));
    });
  }
  const targets = students
    .filter((student) => {
      const phone = cleanPhone(student.phone || student.id);
      if (!phone) return false;
      if (audience === 'district') return String(student.district || '').toLowerCase() === String(district || '').toLowerCase();
      if (audience === 'book') return buyers.has(phone);
      return true;
    })
    .map((student) => cleanPhone(student.phone || student.id));
  await Promise.all(
    [...new Set(targets)].map((phone) =>
      fs.addDoc(fs.collection(db, 'notifications'), {
        phone,
        title,
        body,
        type: 'admin_broadcast',
        read: false,
        audience,
        bookTitle,
        district,
        createdAt: now
      })
    )
  );
  return targets.length;
}

function orderPhoneFromAny(order) {
  return cleanPhone(order.delivery?.phone || order.studentPhone || order.phone || '');
}

function orderItemsText(order) {
  return (order.items || [])
    .map((item) => {
      const title = item.bt || item.bookTitle || 'Book';
      const variant = item.vn || item.variantName || '';
      const qty = Number(item.qty || 1);
      const lineTotal = Number(item.price || 0) * qty;
      return `${title}${variant ? ' - ' + variant : ''} x${qty}${lineTotal ? ' = Rs.' + lineTotal.toLocaleString('en-IN') : ''}`;
    })
    .join('\n');
}

function orderWebhookPayload(order, event) {
  const delivery = order.delivery || {};
  const phone = orderPhoneFromAny(order);
  const total = Number(order.total || orderTotal(order.items || []) || 0);
  const orderId = order.orderId || order.id || '';
  return {
    event,
    source: 'react_platform',
    orderId,
    paymentId: order.paymentId || '',
    studentName: delivery.name || order.studentName || '',
    phone: phone ? '+91' + phone : '',
    whatsapp: '+91' + cleanPhone(delivery.altPhone || phone),
    email: delivery.email || '',
    address: delivery.address || [delivery.street, delivery.postOffice, delivery.district, delivery.state].filter(Boolean).join(', '),
    pincode: delivery.pincode || '',
    district: delivery.district || '',
    state: delivery.state || '',
    items: orderItemsText(order),
    total: total ? 'Rs.' + total.toLocaleString('en-IN') : '',
    deliveryStatus: order.deliveryStatus || order.status || '',
    trackingNumber: order.trackingNumber || '',
    trackingCarrier: order.trackingCarrierName || order.trackingCarrier || 'Professional Couriers',
    trackingUrl: order.trackingUrl || professionalCourierTrackingUrl(order.trackingNumber || ''),
    trackingPageUrl: order.trackingNumber ? `https://books.muppadaitrainingacademy.com${professionalCourierTrackingUrl(order.trackingNumber)}` : 'https://books.muppadaitrainingacademy.com/track',
    orderDate: order.createdAt ? dateLabel(order.createdAt) : new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
    shippedDate: order.shippedAt ? dateLabel(order.shippedAt) : '',
    message: event === 'order_shipped'
      ? `Your books are dispatched via Professional Couriers${order.trackingNumber ? ' with tracking number ' + order.trackingNumber : ''}. You will receive it in 2-4 working days.`
      : 'Payment received and order created successfully.'
  };
}

async function webhookUrlForEvent(event) {
  try {
    const settings = await loadSiteSettings();
    const key = event === 'order_shipped' ? 'shippedWebhookUrl' : 'paymentWebhookUrl';
    if (Object.prototype.hasOwnProperty.call(settings, key)) {
      const configuredUrl = String(settings[key] || '').trim();
      if (configuredUrl) return configuredUrl;
      if (event === 'payment_success') return PABBLY_WEBHOOK_URL;
      return '';
    }
  } catch {
    // Keep the checkout flow independent from settings load failures.
  }
  return PABBLY_WEBHOOK_URL;
}

function webhookResultFields(event, result) {
  const prefix = event === 'order_shipped' ? 'shippedWebhook' : 'paymentWebhook';
  return {
    [`${prefix}Status`]: result?.ok ? 'sent' : result?.status || 'failed',
    [`${prefix}Method`]: result?.method || '',
    [`${prefix}StatusCode`]: result?.statusCode || 0,
    [`${prefix}Error`]: result?.error || '',
    [`${prefix}Response`]: String(result?.body || '').slice(0, 500),
    [`${prefix}UpdatedAt`]: new Date().toISOString()
  };
}

async function saveOrderWebhookResult(order, event, result) {
  const orderDocId = order?.id || order?.orderDocId || '';
  if (!orderDocId) return;
  try {
    await fs.setDoc(fs.doc(db, 'orders', orderDocId), webhookResultFields(event, result), { merge: true });
  } catch {
    // Webhook delivery should not depend on Firestore status logging.
  }
}

async function sendPaymentWebhookViaServer(payload, webhookUrl) {
  const targetUrl = String(webhookUrl || '').trim() || PABBLY_WEBHOOK_URL;
  try {
    const response = await fetch('/api/payment-success-webhook', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, webhookUrl: targetUrl })
    });
    const result = await response.json().catch(() => ({}));
    if (response.ok && result.ok !== false) {
      return {
        ok: true,
        status: 'sent',
        method: 'payment_server',
        statusCode: Number(result.status || response.status || 200),
        body: result.body || ''
      };
    }
    return {
      ok: false,
      status: 'failed',
      method: 'payment_server',
      statusCode: Number(result.status || response.status || 0),
      body: result.body || '',
      error: result.error || result.body || `Payment webhook status ${response.status}`
    };
  } catch (error) {
    return {
      ok: false,
      status: 'failed',
      method: 'payment_server',
      statusCode: 0,
      error: error?.message || 'Payment webhook request failed'
    };
  }
}

export async function sendPabblyWebhook(payload, webhookUrl) {
  const targetUrl = String(webhookUrl || '').trim();
  if (!targetUrl) return { ok: false, status: 'skipped', error: 'Missing webhook URL' };
  const proxyBody = JSON.stringify({ ...payload, webhookUrl: targetUrl });
  const payloadBody = JSON.stringify(payload);
  let proxyError = '';
  try {
    const proxyResponse = await fetch('/api/pabbly', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: proxyBody
    });
    const proxyResult = await proxyResponse.json().catch(() => ({}));
    if (proxyResponse.ok && proxyResult.ok !== false) {
      return {
        ok: true,
        status: 'sent',
        method: 'proxy',
        statusCode: Number(proxyResult.status || proxyResponse.status || 200),
        body: proxyResult.body || ''
      };
    }
    proxyError = proxyResult.error || proxyResult.body || `Proxy status ${proxyResponse.status}`;
  } catch (err) {
    proxyError = err?.message || 'Proxy request failed';
    // Fall back to the direct Pabbly webhook when the Pages Function is unavailable.
  }
  try {
    await fetch(targetUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: payloadBody
    });
    return { ok: true, status: 'sent', method: 'direct_no_cors', statusCode: 0, body: '', error: proxyError };
  } catch (err) {
    console.warn('Pabbly webhook failed:', err?.message || err);
    return { ok: false, status: 'failed', method: 'direct_no_cors', statusCode: 0, error: err?.message || proxyError || 'Webhook failed' };
  }
}

export async function sendPaymentSuccessWebhook(order) {
  const payload = orderWebhookPayload(order, 'payment_success');
  const webhookUrl = await webhookUrlForEvent(payload.event);
  let result = await sendPaymentWebhookViaServer(payload, webhookUrl);
  if (!result.ok) {
    result = await sendPabblyWebhook(payload, webhookUrl);
  }
  await saveOrderWebhookResult(order, payload.event, result);
  return result;
}

export async function ensurePaymentSuccessWebhook(order, { force = false } = {}) {
  if (!order) return { ok: false, status: 'skipped', error: 'Missing order' };
  if (!force && order.paymentWebhookStatus === 'sent') {
    return { ok: true, status: 'already_sent', method: order.paymentWebhookMethod || '' };
  }
  return sendPaymentSuccessWebhook(order);
}

export async function sendOrderShippedWebhook(order) {
  const payload = orderWebhookPayload(order, 'order_shipped');
  const result = await sendPabblyWebhook(payload, await webhookUrlForEvent(payload.event));
  await saveOrderWebhookResult(order, payload.event, result);
  return result;
}

export async function sendShippedNotification(phone, order) {
  const clean = cleanPhone(phone);
  if (!clean) return;
  const orderId = order.orderId || order.id || '';
  const books = (order.items || []).map((i) => i.bt || i.bookTitle || '').filter(Boolean).join(', ');
  const tracking = String(order.trackingNumber || '').trim();
  const shippedOrder = {
    ...order,
    deliveryStatus: 'shipped',
    shippedAt: order.shippedAt || new Date().toISOString()
  };
  try {
    await fs.addDoc(fs.collection(db, 'notifications'), {
      phone: clean,
      title: 'Your order has been shipped!',
      body: `Order ${orderId}${books ? ' - ' + books : ''} is on its way via Professional Couriers${tracking ? ' - Tracking: ' + tracking : ''}. You will receive it in 2-4 working days.`,
      type: 'order_shipped',
      read: false,
      orderId,
      trackingNumber: tracking,
      createdAt: new Date().toISOString()
    });
  } finally {
    sendOrderShippedWebhook(shippedOrder).catch(() => {});
  }
}
